import React, { useMemo } from 'react';
import { Route, Switch, useLocation, useParams } from 'react-router-dom';
import cx from 'classnames';
import { getFlyingBetslipConfig, sportsbookRouterListenerCb } from 'helpers/prematch';

import {
  createSportsbookState,
  get,
  PreferencesProvider,
  SelectedSportsbookSportProvider,
  SiteConfigManager,
  SportsbookProvider,
  useCreatePreferencesState,
  useSettings,
} from '@atlas/core';
import {
  COMPONENT_TYPES,
  SelectionsRedirect,
  SportsbookRouter,
  sportsbookRoutes,
} from '@atlas/routing';
import { ScrollableContainer } from '@atlas/ui';

import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import { cmdSymbol, searchQuerySymbol, SIMPLIFIED_SPORTSBOOK_PATH_PATTERN } from 'common/constants';
import SportsList from 'common/Widgets/SportsList/SportsList';
import SportsTimeFilter from 'common/Widgets/SportsTimeFilter/SportsTimeFilter';
import TopEvents from 'common/Widgets/TopEvents';
import SportsbookFavorites from 'components/FavoriteEvents/SportsbookFavorites';
import FreeBetNotification from 'components/FreeBetNotification/FreeBetNotification';
import SportsbookMaintenanceContainer from 'components/MaintenanceContainer/SportsbookMaintenanceContainer';
import DetailsPage from 'components/Pages/Details';
import EmptySportView from 'components/Pages/EmptySportView';
import Prematch from 'components/Pages/Prematch/Prematch';
import SelectedTournamentView from 'components/Pages/Prematch/SelectedTournamentView';
import SimplifiedSportsbookView from 'components/Pages/Prematch/SimplifiedSportsbookView';
import SportLoader from 'components/Pages/Prematch/SportLoader';
import { SportsbookBanner } from 'components/Pages/Prematch/SportsbookBanner';
import PrematchSportsbookLoader from 'components/Pages/PrematchSportsbookLoader';
import {
  SelectedEventQueryRouteWrapper,
  SelectedEventQueryViewWrapper,
  SelectedProviderWrapper,
} from 'components/Pages/SelectedWrappers';
import SportItemDestruction from 'components/Pages/SportItemDestruction';
import { DefaultSportsbookNavigation } from 'components/Pages/SportsbookNavigation';
import { CategoryRedirect, SportRedirect } from 'components/Pages/SportsbookRedirects';
import { StandardSearch } from 'components/Search';
import SportsbookSelections from 'components/SportsbookSelections/SportsbookSelections';

const { SPORT_PREMATCH } = COMPONENT_TYPES;

const PrematchSportsbookProvider = props => {
  const { children, sportServices } = props;

  const { categoryName, sportName, tournamentName } = useParams();

  const sportbookState = createSportsbookState({
    id: 'sport',
    selectedCategoryUrlName: categoryName,
    selectedSportUrlName: sportName,
    selectedTournamentUrlName: tournamentName,
    sportServices,
  });

  return <SportsbookProvider value={sportbookState}>{children}</SportsbookProvider>;
};

const PrematchSportsbookView = () => {
  const { footerHeight, footerUrl, quickBet: isQuickBet } = useSettings();
  const { betIdToEdit } = useLocalBetslip();

  const location = useLocation();
  const { search } = location;
  const isCmdLink = search.includes(cmdSymbol);
  const searchParams = new URLSearchParams(search);

  const isSearchActive = !!(search && searchParams.get(searchQuerySymbol));

  const isEnableSearchMobile = React.useMemo(
    () => SiteConfigManager.getConfig('config.config.sportsbook.enableSearchMobile'),
    [],
  );

  const layoutToScrollClass = useMemo(
    () =>
      cx('layout-to-scroll full-height', {
        hidden: isSearchActive,
        'layout-to-scroll-with-padding': getFlyingBetslipConfig().hasExtended,
        quickbet: isQuickBet || betIdToEdit,
      }),
    [betIdToEdit, isQuickBet, isSearchActive],
  );

  const { topEvents } = SiteConfigManager.getConfig('config.pages', []).find(
    page => page.type === COMPONENT_TYPES.SPORT_PREMATCH,
  ).options;

  const prematchPath = get(
    SiteConfigManager.getConfig('config.pages', []).find(({ type }) => type === SPORT_PREMATCH),
    'path',
  );

  const isEnableTimeFilterMobile = React.useMemo(
    () => SiteConfigManager.getConfig('config.config.sportsbook.enableTimeFilterMobile'),
    [],
  );

  const isCouponsActive = SiteConfigManager.getConfig('config.config.sportsbook.isCouponsActive');
  const openCouponsByDefault = SiteConfigManager.getConfig(
    'config.config.sportsbook.openCouponsByDefault',
  );

  const footerIframe = useMemo(() => {
    if (footerUrl) {
      const options = {
        frameBorder: '0',
        src: footerUrl,
        width: '100%',
      };

      if (footerHeight) {
        options.height = footerHeight;
      }

      return <iframe {...options} />;
    }

    return null;
  }, [footerUrl, footerHeight]);

  const isSelectionsRedirect = isCouponsActive && openCouponsByDefault && !isCmdLink;

  const content = (
    <PrematchSportsbookLoader>
      <ScrollableContainer className={layoutToScrollClass} id="content">
        <FreeBetNotification />

        <SelectedProviderWrapper Component={SelectedSportsbookSportProvider}>
          <Route path={`${prematchPath}/:sportName/:categoryName/:tournamentName`}>
            <SportLoader className="absolute">
              <SelectedTournamentView />
            </SportLoader>
          </Route>
        </SelectedProviderWrapper>

        <Switch>
          <Route exact path={`${prematchPath}/:sportName/:categoryName?`}>
            {isEnableSearchMobile && <StandardSearch />}

            <SportsbookBanner className="sportsbook-banner-wrapper" />

            {topEvents && <TopEvents />}
            <DefaultSportsbookNavigation path={prematchPath}>
              {isEnableTimeFilterMobile && <SportsTimeFilter />}
              <span className="sport-scroll" />
              <SportsList />

              <div className="sportsbook-content">
                <SelectedProviderWrapper Component={SelectedSportsbookSportProvider}>
                  <Switch>
                    <Route
                      component={SportsbookFavorites}
                      path={`${prematchPath}/${sportsbookRoutes.favorites}`}
                    />

                    {isCouponsActive && (
                      <Route
                        component={SportsbookSelections}
                        path={`${prematchPath}/${sportsbookRoutes.selections}/:selectionId?`}
                      />
                    )}

                    <EmptySportView>
                      <SportLoader className="absolute">
                        <Route
                          component={Prematch}
                          exact
                          path={`${prematchPath}/:sportName/:categoryName`}
                        />
                        <Route>
                          <SportLoader>
                            <CategoryRedirect />
                            <SportItemDestruction />
                          </SportLoader>
                        </Route>
                      </SportLoader>
                    </EmptySportView>
                  </Switch>
                </SelectedProviderWrapper>
              </div>

              {footerIframe}
            </DefaultSportsbookNavigation>
          </Route>
          <SelectionsRedirect isRedirect={isSelectionsRedirect} path={prematchPath}>
            <SportRedirect />
          </SelectionsRedirect>
        </Switch>
      </ScrollableContainer>
    </PrematchSportsbookLoader>
  );

  return (
    <SelectedEventQueryViewWrapper fallback={content} location={location}>
      <SelectedEventQueryRouteWrapper>
        <DetailsPage />
      </SelectedEventQueryRouteWrapper>
    </SelectedEventQueryViewWrapper>
  );
};

const PrematchSportsbook = () => {
  const favoritesState = useCreatePreferencesState();

  const isSimplifiedView = SiteConfigManager.getConfig(
    'config.config.sportsbook.mobileGridViewType',
  );

  const props = {};
  if (isSimplifiedView === 'simplified') {
    props.path = SIMPLIFIED_SPORTSBOOK_PATH_PATTERN;
  }

  const sportServices = ['LIVE', 'PREMATCH'];

  return (
    <SportsbookMaintenanceContainer>
      <SportsbookRouter
        isListenRouterNavigation
        sportsbookRouterListenerCb={sportsbookRouterListenerCb}
        {...props}
      >
        <PreferencesProvider value={favoritesState}>
          <PrematchSportsbookProvider sportServices={sportServices}>
            {isSimplifiedView === 'simplified' ? (
              <SimplifiedSportsbookView path="/sportsbook" />
            ) : (
              <PrematchSportsbookView />
            )}
          </PrematchSportsbookProvider>
        </PreferencesProvider>
      </SportsbookRouter>
    </SportsbookMaintenanceContainer>
  );
};

export default PrematchSportsbook;
